import { useEffect, useMemo, useRef, useState } from "react";

const HOLD_DELAY = 300;
const MOVE_THRESHOLD = 8;

const IDLE_STATE = {
  pointerId: null,
  startX: 0,
  startY: 0,
  fromIndex: null,
  overIndex: null,
  active: false,
};

// Find the row under a viewport coordinate, skipping the dragged row itself.
const rowIndexAtPoint = (clientX, clientY) => {
  const els = document.elementsFromPoint(clientX, clientY);
  for (const el of els) {
    const rowEl = el.closest && el.closest("[data-drag-row]");
    if (!rowEl || rowEl.dataset.dragged === "true") continue;
    const parsed = parseInt(rowEl.getAttribute("data-drag-row"), 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return null;
};

/**
 * Reorder a vertical list by press-and-hold dragging (touch or mouse), an
 * instant-drag grip handle, or native HTML5 drag-and-drop.
 */
export default function usePressDragReorder({ items, getId, onReorder }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const holdTimerRef = useRef(null);
  const dragStateRef = useRef({ ...IDLE_STATE });
  const itemsRef = useRef(items);
  const onReorderRef = useRef(onReorder);
  useEffect(() => {
    itemsRef.current = items;
    onReorderRef.current = onReorder;
  }, [items, onReorder]);

  const docHandlersRef = useRef(null);
  if (docHandlersRef.current === null) {
    const detach = () => {
      const h = docHandlersRef.current;
      document.removeEventListener("pointermove", h.onMove);
      document.removeEventListener("pointerup", h.onUp);
      document.removeEventListener("pointercancel", h.onCancel);
      document.removeEventListener("touchmove", h.onTouchMove);
      document.removeEventListener("touchend", h.onUp);
      document.removeEventListener("touchcancel", h.onCancel);
    };

    // Clear every trace of the gesture without committing a reorder.
    const reset = () => {
      clearTimeout(holdTimerRef.current);
      dragStateRef.current = { ...IDLE_STATE };
      setDragIndex(null);
      setDragOverIndex(null);
      detach();
    };

    // Releasing a drag can synthesize a click on whatever ends up under the
    const suppressNextClick = () => {
      const swallow = (ev) => {
        ev.stopPropagation();
        ev.preventDefault();
      };
      document.addEventListener("click", swallow, { capture: true, once: true });
      setTimeout(
        () => document.removeEventListener("click", swallow, { capture: true }),
        400
      );
    };

    const commitAndReset = () => {
      const { fromIndex, overIndex, active } = dragStateRef.current;
      reset();
      if (
        active &&
        fromIndex !== null &&
        overIndex !== null &&
        fromIndex !== overIndex
      ) {
        const current = itemsRef.current || [];
        if (overIndex >= 0 && overIndex < current.length) {
          const reordered = [...current];
          const [moved] = reordered.splice(fromIndex, 1);
          reordered.splice(overIndex, 0, moved);
          onReorderRef.current(reordered);
        }
      }
      if (active) suppressNextClick();
    };

    const updateFromCoords = (clientX, clientY) => {
      const state = dragStateRef.current;
      if (!state.active) return;
      const idx = rowIndexAtPoint(clientX, clientY);
      if (idx === null || idx === state.overIndex) return;
      state.overIndex = idx;
      setDragOverIndex(idx);
    };

    const onMove = (e) => {
      if (!dragStateRef.current.active) return;
      if (e.cancelable) e.preventDefault();
      updateFromCoords(e.clientX, e.clientY);
    };
    const onTouchMove = (e) => {
      // Pending hold: let the browser scroll (its pointercancel aborts the hold)
      if (!dragStateRef.current.active) return;
      if (e.cancelable) e.preventDefault();
      const t = e.touches && e.touches[0];
      if (t) updateFromCoords(t.clientX, t.clientY);
    };
    const onUp = () => commitAndReset();
    // Browser reclaimed the gesture (scroll, system UI)
    const onCancel = () => reset();

    docHandlersRef.current = { onMove, onTouchMove, onUp, onCancel, detach, reset };
  }

  const attachDocListeners = () => {
    const h = docHandlersRef.current;
    h.detach();
    document.addEventListener("pointermove", h.onMove, { passive: false });
    document.addEventListener("pointerup", h.onUp);
    document.addEventListener("pointercancel", h.onCancel);
    document.addEventListener("touchmove", h.onTouchMove, { passive: false });
    document.addEventListener("touchend", h.onUp);
    document.addEventListener("touchcancel", h.onCancel);
  };

  const beginGesture = (e, index, active) => {
    docHandlersRef.current.reset();
    dragStateRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      fromIndex: index,
      overIndex: index,
      active,
    };
    attachDocListeners();
  };

  const handleRowPointerDown = (e, index) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    beginGesture(e, index, false);
    holdTimerRef.current = setTimeout(() => {
      const state = dragStateRef.current;
      if (state.pointerId === null) return;
      state.active = true;
      setDragIndex(index);
      setDragOverIndex(index);
    }, HOLD_DELAY);
  };

  // Grip handle: instant drag, no hold. touch-action: none on the handle
  const handleGripPointerDown = (e, index) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.stopPropagation();
    beginGesture(e, index, true);
    setDragIndex(index);
    setDragOverIndex(index);
  };

  const handleRowPointerMove = (e) => {
    const state = dragStateRef.current;
    if (state.pointerId === null || state.active) return;
    const dx = Math.abs(e.clientX - state.startX);
    const dy = Math.abs(e.clientY - state.startY);
    if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
      docHandlersRef.current.reset();
    }
  };

  // Clean up if the component unmounts mid-drag.
  useEffect(() => {
    return () => {
      const h = docHandlersRef.current;
      if (h) h.detach();
      clearTimeout(holdTimerRef.current);
    };
  }, []);

  // HTML5 drag-and-drop
  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== index) setDragOverIndex(index);
  };
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const fromIndex = dragIndex;
    setDragIndex(null);
    setDragOverIndex(null);
    if (
      fromIndex === null ||
      dropIndex === null ||
      fromIndex === dropIndex ||
      dropIndex < 0 ||
      dropIndex >= items.length
    ) {
      return;
    }
    const reordered = [...items];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    onReorder(reordered);
  };
  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // The list with the in-flight move applied, so the dragged row visually follows the pointer.
  const displayItems = useMemo(() => {
    if (
      dragIndex === null ||
      dragOverIndex === null ||
      dragIndex === dragOverIndex ||
      dragIndex < 0 ||
      dragIndex >= items.length ||
      dragOverIndex < 0 ||
      dragOverIndex >= items.length
    ) {
      return items;
    }
    const reordered = [...items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dragOverIndex, 0, moved);
    return reordered;
  }, [items, dragIndex, dragOverIndex]);

  const draggedId =
    dragIndex !== null && items[dragIndex] ? getId(items[dragIndex]) : null;

  const getRowProps = (originalIndex, displayIndex) => ({
    "data-drag-row": displayIndex,
    "data-dragged": originalIndex === dragIndex ? "true" : "false",
    draggable: true,
    onDragStart: () => handleDragStart(originalIndex),
    onDragOver: (e) => handleDragOver(e, displayIndex),
    onDrop: (e) => handleDrop(e, displayIndex),
    onDragEnd: handleDragEnd,
    onPointerDown: (e) => handleRowPointerDown(e, originalIndex),
    onPointerMove: handleRowPointerMove,
    onContextMenu: (e) => e.preventDefault(),
    style: {
      touchAction: originalIndex === dragIndex ? "none" : "auto",
      // Suppress the OS long-press callout/selection so press-and-hold
      // starts the drag instead.
      WebkitTouchCallout: "none",
      WebkitUserSelect: "none",
    },
  });

  const getGripProps = (originalIndex) => ({
    onPointerDown: (e) => handleGripPointerDown(e, originalIndex),
    style: { touchAction: "none" },
  });

  return { displayItems, draggedId, getRowProps, getGripProps };
}
